import { Cluster, SelectMenu, StatusBadge } from '../ui';
import { useOperatorWorkspaces } from '../lib/operatorWorkspace';

/**
 * Operator-only workspace dropdown (§14.6, #278) shown on the Agents + Runs pages so a
 * platform OPERATOR can view ANY workspace's agents + runs. Gated TRY-LOAD-THEN-HIDE: it
 * queries the operator-only `GET /api/admin/workspaces`; on 403 / any error / an empty
 * list it renders NOTHING, so a non-operator never sees a dropdown (the page is
 * byte-for-byte identical to before). Options are every workspace labelled by owner
 * ("Acme — jane@acme.com"); the caller wires `value` to the scoped workspace and persists
 * the choice as a `ws` URL search param. When a FOREIGN workspace is selected, a small
 * "Viewing {owner} — read-only" badge makes the cross-tenant, view-only mode explicit.
 */
export interface OperatorWorkspaceSelectProps {
  /** The currently-scoped workspace id (the caller's own workspace by default). */
  value: string;
  /** The caller's own workspace — selecting it clears the `ws` override. */
  ownWorkspaceId: string;
  /** Persist the new selection (the caller folds it into the page's `ws` search param). */
  onChange: (workspaceId: string) => void;
  /**
   * #326: whether to show the "Viewing {owner} — read-only" badge on a foreign workspace.
   * Default true (the Runs pages, where operators stay VIEW-ONLY). The Agents pages pass
   * `false` — there operators can now EDIT (support mode), so the "read-only" label would
   * be misleading; the persistent support-mode banner conveys the state instead.
   */
  showForeignBadge?: boolean;
}

export function OperatorWorkspaceSelect({
  value,
  ownWorkspaceId,
  onChange,
  showForeignBadge = true,
}: OperatorWorkspaceSelectProps) {
  const ops = useOperatorWorkspaces();
  // try-load-then-hide: a non-operator (403), a network error, or a still-loading list
  // renders NO dropdown — the operator UI never leaks to a non-operator.
  if (ops.isPending || ops.isError) return null;
  const workspaces = ops.data;
  if (workspaces.length === 0) return null;

  const options = workspaces.map((w) => ({
    value: w.id,
    label: `${w.name} — ${w.owner.email}`,
  }));
  const selected = workspaces.find((w) => w.id === value);
  const viewOnly = value !== ownWorkspaceId;

  return (
    <Cluster gap="3" align="center" wrap>
      <SelectMenu
        value={value}
        onChange={onChange}
        options={options}
        label="View another workspace"
        size="sm"
        minWidth="16rem"
        data-testid="operator-workspace-select"
      />
      {viewOnly && selected && showForeignBadge && (
        <StatusBadge
          tone="warning"
          size="sm"
          label={`Viewing ${selected.owner.name || selected.owner.email} — read-only`}
          data-testid="operator-view-only"
        />
      )}
    </Cluster>
  );
}
